import styled from 'styled-components';

import Button from 'components/shared/Button';
import { Actions } from 'components/shared/StandardModal';
import { WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

interface StepProps {
  // The impacted steps (Workflow#impactedActionsBy) the save would restart.
  actions: WorkflowAction[];
  saving: boolean;
  onConfirm: () => void;
  onBack: () => void;
  backLabel?: string;
}

// The consent half of the correction cascade (design §9, 002 §6): one decision covers
// the whole ripple. Content-only — ReferralModal renders it as the second step of its
// wizard (no modal-on-modal). Confirming sends the save with these action ids as
// restart_action_ids; going back saves nothing and preserves the person's edits.
export function RestartConsentStep({ actions, saving, onConfirm, onBack, backLabel = 'Back' }: StepProps) {
  // Fanned-out (per-building) steps collapse into one line with the affected count —
  // "Review Clinical Assessment (3 buildings)" — instead of repeating the label per
  // building. Order of first appearance is preserved.
  const rows: { label: string; count: number; fanned: boolean }[] = [];
  actions.forEach((action) => {
    const row = rows.find((entry) => entry.label === action.displayLabel);
    if (row) {
      row.count += 1;
      row.fanned = row.fanned && action.groupId !== null;
    } else {
      rows.push({ label: action.displayLabel, count: 1, fanned: action.groupId !== null });
    }
  });

  return (
    <div data-testid='restart-consent-step'>
      <Body>This change affects steps that already ran. Saving will restart:</Body>
      <StepList data-testid='restart-consent-list'>
        {rows.map((row) => (
          <li key={row.label}>
            {row.label}
            {row.count > 1 && <Count> ({row.fanned ? `${row.count} buildings` : `×${row.count}`})</Count>}
          </li>
        ))}
      </StepList>
      <Explain>
        Their current results will be cleared, and each step will run again as its information becomes available. Any
        detail entered manually will be retained.
      </Explain>
      <Actions>
        <Button variant='ghost' onClick={onBack} data-testid='restart-back'>
          {backLabel}
        </Button>
        <Button onClick={onConfirm} disabled={saving} loading={saving} data-testid='restart-confirm'>
          Save and restart
        </Button>
      </Actions>
    </div>
  );
}

const StepList = styled.ul`
  margin: 12px 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
`;

const Count = styled.span`
  font-weight: 400;
  color: ${colors.black50};
`;

const Explain = styled(BodySmall)`
  display: block;
  margin-bottom: 16px;
  color: ${colors.black75};
`;
